import * as React from "react"
import { format, parse, isValid } from "date-fns"
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
]

interface MonthPickerProps {
  /** Value in YYYY-MM format (e.g., "2025-01") */
  value?: string | null
  /** Callback with YYYY-MM format */
  onChange?: (value: string | null) => void
  /** Placeholder text when no value selected */
  placeholder?: string
  /** Additional className for the trigger button */
  className?: string
  /** Whether the picker is disabled */
  disabled?: boolean
  /** ID for form label association */
  id?: string
}

const MonthPicker = React.forwardRef<HTMLButtonElement, MonthPickerProps>(
  ({ value, onChange, placeholder = "Select month", className, disabled, id }, ref) => {
    const [open, setOpen] = React.useState(false)
    const [viewYear, setViewYear] = React.useState(() => {
      if (value) {
        const parsed = parse(value, "yyyy-MM", new Date())
        if (isValid(parsed)) {
          return parsed.getFullYear()
        }
      }
      return new Date().getFullYear()
    })

    // Parse the current value
    const selectedDate = React.useMemo(() => {
      if (!value) return null
      const parsed = parse(value, "yyyy-MM", new Date())
      return isValid(parsed) ? parsed : null
    }, [value])

    // Update view year when value changes externally
    React.useEffect(() => {
      if (selectedDate) {
        setViewYear(selectedDate.getFullYear())
      }
    }, [selectedDate])

    const handleMonthSelect = (monthIndex: number) => {
      const newValue = `${viewYear}-${String(monthIndex + 1).padStart(2, "0")}`
      onChange?.(newValue)
      setOpen(false)
    }

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation()
      onChange?.(null)
    }

    const displayValue = selectedDate
      ? format(selectedDate, "MMM yyyy")
      : null

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            id={id}
            variant="outline"
            disabled={disabled}
            className={cn(
              "w-full justify-start text-left font-normal",
              !displayValue && "text-gray-500",
              className
            )}
          >
            <Calendar className="mr-2 h-4 w-4" />
            {displayValue ?? placeholder}
            {displayValue && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleClear(e as unknown as React.MouseEvent)
                  }
                }}
                className="ml-auto h-4 w-4 rounded-full hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
                aria-label="Clear selection"
              >
                &times;
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="start">
          {/* Year navigation */}
          <div className="flex items-center justify-between mb-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setViewYear(y => y - 1)}
              aria-label="Previous year"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="font-semibold text-sm">{viewYear}</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setViewYear(y => y + 1)}
              aria-label="Next year"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Month grid */}
          <div className="grid grid-cols-3 gap-2">
            {MONTHS.map((month, index) => {
              const isSelected =
                selectedDate &&
                selectedDate.getFullYear() === viewYear &&
                selectedDate.getMonth() === index

              return (
                <Button
                  key={month}
                  variant={isSelected ? "default" : "ghost"}
                  size="sm"
                  onClick={() => handleMonthSelect(index)}
                  className={cn(
                    "h-8 text-xs",
                    isSelected && "bg-blue-600 text-white hover:bg-blue-700"
                  )}
                >
                  {month}
                </Button>
              )
            })}
          </div>
        </PopoverContent>
      </Popover>
    )
  }
)

MonthPicker.displayName = "MonthPicker"

export { MonthPicker }
